import { Box, Dialog, DialogContent, DialogProps, Typography, Icon } from '@mui/material';
import { FC, useState } from 'react';
import { InformationDialogTitle } from '../../../../lib/components/compound/dialogs/informationDialog/title';
import { theme } from '../../../../lib/theme';
import { CalendarEditIcon, CompaniesIcon } from '../../../../lib/icons';
import { CurrencyField, DatePicker } from '../../../../lib/components';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../../lib/components/atomic/button';

type Props = {
  title?: string;
  confirmButtonText: string;
  cancelButtonText: string;
  onConfirm?: (companyAmountData: [number, number, number]) => void;
  onClose?: () => void;
  onCancel?: () => void;
  amount?: number;
  monthDeactivated?: boolean;
  month?: Date;
} & Pick<DialogProps, 'open'>;

export const AutomaticAssignmentDialog: FC<Props> = ({
  open,
  title,
  confirmButtonText,
  cancelButtonText,
  onConfirm,
  onClose,
  onCancel,
  amount,
  monthDeactivated = false,
  month
}) => {
  const { t } = useTranslation();
  const nextMonth = new Date(new Date().setMonth(new Date().getMonth() + 1, 1));

  const handleConfirm = () => {
    if (!!companyAmount || companyAmount === 0)
      onConfirm?.([companyAmount, selectedMonth, selectedYear]);
  };

  const handleCancel = () => {
    onCancel?.();
    onClose?.();
  };

  const [selectedMonth, setSelectedMonth] = useState<number>((month ?? nextMonth).getMonth());
  const [selectedYear, setSelectedYear] = useState<number>((month ?? nextMonth).getFullYear());
  const [companyAmount, setCompanyAmount] = useState<number>();
  const [error, setError] = useState(false);
  const [date, setDate] = useState<string>((month ?? nextMonth).toString());

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      PaperProps={{
        className: 'flex flex-col justify-center max-h-[80vh] max-w-[80vw] shadow-900'
      }}
    >
      {title && <InformationDialogTitle title={title} onClose={onClose} />}
      <DialogContent
        dividers
        sx={{
          borderBottom: 'none',
          paddingTop: '8px',
          paddingBottom: '8px',
          overflow: 'auto'
        }}
      >
        <Box
          sx={{
            marginTop: '14px',
            marginBottom: '30px',
            borderRadius: '15px',
            borderColor: theme.palette.primary.light,
            borderWidth: '2px',
            borderStyle: 'solid',
            fontFamily: 'Rubik',
            padding: '14px',
            position: 'relative'
          }}
        >
          <Icon
            sx={{
              position: 'absolute',
              top: '-24px',
              left: '12%',
              transform: 'translateX(-50%)',
              backgroundColor: 'white',
              padding: '2px',
              borderRadius: '50%',
              zIndex: 1,
              fontSize: '36px'
            }}
          >
            <CompaniesIcon color={theme.palette.primary.light} sx={{ fontSize: 'inherit' }} />
          </Icon>
          <Typography
            noWrap
            style={{
              marginRight: '2rem',
              fontSize: '11px',
              color: theme.palette.grey['500']
            }}
          >
            {t('custom:default_amount')}
          </Typography>
          <CurrencyField
            style={{ height: '40px' }}
            value={amount ? amount / 100 : undefined}
            shiftDecimals={2}
            returnInteger={true}
            onChange={(event) => setCompanyAmount(Number(event.target.value))}
          />
        </Box>
        <Box
          sx={{
            marginY: '8px',
            borderRadius: '15px',
            borderColor: theme.palette.primary.light,
            borderWidth: '2px',
            borderStyle: 'solid',
            fontFamily: 'Rubik',
            padding: '14px',
            position: 'relative'
          }}
        >
          <Icon
            sx={{
              position: 'absolute',
              top: '-24px',
              left: '12%',
              transform: 'translateX(-50%)',
              backgroundColor: 'white',
              padding: '2px',
              borderRadius: '50%',
              zIndex: 1,
              fontSize: '36px'
            }}
          >
            <CalendarEditIcon color={theme.palette.primary.light} sx={{ fontSize: 'inherit' }} />
          </Icon>
          <Typography
            noWrap
            style={{
              marginRight: '2rem',
              fontSize: '11px',
              color: theme.palette.grey['500']
            }}
          >
            {t('custom:all_assignments_valid_from')}
          </Typography>
          <DatePicker
            variant="month"
            initialValue={month ? month.toString() : date}
            onChange={(dateStr) => {
              setError(false);
              setDate(dateStr);
              setSelectedMonth(new Date(dateStr).getMonth());
              setSelectedYear(new Date(dateStr).getFullYear());
            }}
            onChangeInvalidDate={() => setError(true)}
            helperText={t('form:INVALID_DATE')}
            error={error}
            minDate={nextMonth}
            disablePast={true}
            disabled={monthDeactivated}
          />
        </Box>

        <Box
          sx={{
            backgroundColor: theme.palette.grey[50],
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '12px'
          }}
        >
          <Typography variant="h6" gutterBottom>
            {t('custom:automatic_assignment_hint_header')}
          </Typography>
          <Typography variant="body2" sx={{ maxWidth: '50vh' }}>
            {t('custom:automatic_assignment_hint_description')}
          </Typography>
          <Typography variant="body2" sx={{ marginTop: '8px' }}>
            {t('custom:automatic_assignment_hint_footer')}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            width: '100%',
            marginTop: '16px',
            marginBottom: '16px'
          }}
        >
          <Button variant="secondary" onClick={handleCancel}>
            {cancelButtonText}
          </Button>
          <Button disabled={!companyAmount && companyAmount !== 0} onClick={handleConfirm}>
            {confirmButtonText}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
